import {Fragment, useEffect, useRef, useState, useLayoutEffect} from 'react';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {Navbar2} from '@common/ui/navigation/navbar/navbar2';
import './landing.css';
import {Footer} from '@common/ui/footer/footer';
import {m, AnimatePresence} from 'framer-motion';
import {BillingCycleRadio} from '@common/billing/pricing-table/billing-cycle-radio';
import {PricingTable} from '@common/billing/pricing-table/pricing-table';
import {UpsellBillingCycle} from '@common/billing/pricing-table/find-best-price';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {useAuth} from '@common/auth/use-auth';
import {useAuthModal} from '@common/auth/ui/auth-modal/auth-modal-context';
import {useNavigate} from '@common/utils/hooks/use-navigate';

// ─────────────────────────────────────────────
//  MAIN PAGE
// ─────────────────────────────────────────────
export function LandingPage() {
  return (
    <Fragment>
      <DefaultMetaTags />
      <div className="rp-root">
        <Navbar2 topOffsetClass="top-0" />
        <HeroSection />
        <LogoStrip />
        <ShowcaseSection />
        <PlatformSection />
        <TestimonialSection />
        <PricingSection />
        <CtaSection />
      </div>
      <Footer />
    </Fragment>
  );
}

// ─────────────────────────────────────────────
//  HERO SECTION
// ─────────────────────────────────────────────
type HeroCategory = {icon: () => JSX.Element; label: string};

const HERO_CATEGORIES: HeroCategory[] = [
  {label: 'Website',      icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="4" width="18" height="14" rx="3"/><path d="M8 20h8M12 18v2"/></svg>},
  {label: 'Mobile',       icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="7" y="2" width="10" height="20" rx="3"/><circle cx="12" cy="18" r="1" fill="currentColor"/></svg>},
  {label: 'Design',       icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>},
  {label: 'Slides',       icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="2" y="4" width="20" height="14" rx="2"/><path d="M8 18l2 3M16 18l-2 3"/></svg>},
  {label: 'Animation',    icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>},
  {label: 'Portfolio',    icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>},
  {label: 'Commerce',   icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>},
  {label: 'Dashboard',    icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>},
  {label: 'Blog',         icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>},
  {label: 'Landing', icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>},
  {label: 'Game',         icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="6" y1="12" x2="10" y2="12"/><line x1="8" y1="10" x2="8" y2="14"/><circle cx="15" cy="11" r="1" fill="currentColor"/><circle cx="17" cy="13" r="1" fill="currentColor"/><path d="M6 9h12a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4a2 2 0 012-2z"/></svg>},
  {label: 'Form',         icon: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 9h10M7 13h6"/></svg>},
];

const CATEGORY_PROMPTS: Record<string, string[]> = {
  _default:        ['Checkout flow prototype', 'Personal portfolio site', 'Analytics dashboard'],
  Website:         ['Personal portfolio with dark mode', 'Agency homepage with animations', 'SaaS landing with pricing section'],
  Mobile:          ['Fitness tracking app UI', 'Food delivery order flow', 'Chat messaging screen'],
  Design:          ['Brand identity system', 'UI component library', 'Icon set with 24 icons'],
  Slides:          ['Startup pitch deck', 'Product launch presentation', 'Quarterly review slides'],
  Animation:       ['3D product showcase', 'Animated hero section', 'Scroll-driven storytelling page'],
  Portfolio:       ['Creative developer portfolio', 'Photographer showcase site', 'UX designer case studies'],
  'E-commerce':    ['Fashion store storefront', 'Product detail page', 'Shopping cart & checkout flow'],
  Dashboard:       ['Analytics admin panel', 'SaaS metrics dashboard', 'Finance tracker with charts'],
  Blog:            ['Tech blog with dark mode', 'Newsletter landing page', 'Long-form article layout'],
  'Landing Page':  ['SaaS product launch page', 'App download landing page', 'Event registration page'],
  Game:            ['3D racing game', 'Word puzzle with animations', 'Tower defense prototype'],
  Form:            ['Multi-step onboarding form', 'Contact & booking form', 'Survey with conditional logic'],
};

/** sessionStorage key used by new-project-page to restore AI builder state */
const BUILDER_STORAGE_KEY = 'be.newProjectState';

function savePromptToSession(prompt: string) {
  try {
    sessionStorage.setItem(
      BUILDER_STORAGE_KEY,
      JSON.stringify({mode: 'ai', aiPrompt: prompt, aiInputMode: 'free'}),
    );
  } catch (_) {}
}

function HeroSection() {
  const [query, setQuery] = useState('');
  const [activeChip, setActiveChip] = useState<string | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const {isLoggedIn} = useAuth();
  const authModal = useAuthModal();
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);

  const scrollChips = (dir: 1 | -1) => {
    chipsRef.current?.scrollBy({left: dir * 220, behavior: 'smooth'});
  };

  // Auto-resize textarea
  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const handleChip = (label: string) => {
    // Toggle: re-clicking the active chip deselects it
    setActiveChip(prev => (prev === label ? null : label));
  };

  const handleHeroSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) {
      textareaRef.current?.focus();
      return;
    }

    if (isLoggedIn) {
      // Save to sessionStorage so new-project-page restores it
      savePromptToSession(trimmed);
      setIsBuilding(true);
      // Brief animation then navigate
      setTimeout(() => {
        navigate('/new');
      }, 600);
      return;
    }

    // Not logged in — save prompt and open auth modal with prompt preview
    savePromptToSession(trimmed);
    authModal?.open({
      mode: 'register',
      subtitle: 'We saved your prompt! Create an account to start building.',
      savedPrompt: trimmed.length > 120 ? trimmed.slice(0, 117) + '…' : trimmed,
      redirectTo: '/new',
    });
  };

  return (
    <section className="rp-hero">
      <m.div
        className="rp-hero__inner"
        initial={{opacity: 0, y: 8}}
        animate={{opacity: 1, y: 0}}
        transition={{duration: 0.4, ease: [0.16, 1, 0.3, 1]}}
      >
        <div className="rp-hero__badge">
          <span className="rp-badge-dot" />
          Now with AI agents
        </div>
        <h1 className="rp-hero__h1">What will you build?</h1>
        <p className="rp-hero__sub">Turn ideas into apps in minutes — no coding needed</p>

        <div className="rp-hero__input-wrap">
          <m.form
            className={`rp-hero__form${isBuilding ? ' rp-hero__form--building' : ''}`}
            onSubmit={handleHeroSubmit}
            animate={isBuilding ? {scale: 0.995, opacity: 0.7} : {scale: 1, opacity: 1}}
            transition={{duration: 0.25, ease: 'easeInOut'}}
            style={{position: 'relative'}}
          >
            {/* Building overlay */}
            <AnimatePresence>
              {isBuilding && (
                <m.div
                  className="rp-hero__building-overlay"
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  exit={{opacity: 0}}
                  transition={{duration: 0.2}}
                >
                  <div className="rp-spinner" />
                  <span className="rp-hero__building-label">
                    Launching builder
                    <span className="rp-dot-pulse">
                      <span /><span /><span />
                    </span>
                  </span>
                </m.div>
              )}
            </AnimatePresence>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              className="rp-hero__textarea"
              rows={2}
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                autoResize();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleHeroSubmit(e as any);
                }
              }}
              placeholder="Describe your idea, we'll bring it to life..."
              disabled={isBuilding}
            />

            {/* Bottom bar */}
            <div className="rp-hero__form-footer">
              {/* Active chip tag */}
              <AnimatePresence mode="popLayout">
                {activeChip ? (
                  <m.div
                    key={activeChip}
                    className="rp-hero__chip-tag"
                    initial={{opacity: 0, scale: 0.8, x: -4}}
                    animate={{opacity: 1, scale: 1, x: 0}}
                    exit={{opacity: 0, scale: 0.8, x: -4}}
                    transition={{duration: 0.18, ease: [0.16, 1, 0.3, 1]}}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <rect x="7" y="2" width="10" height="20" rx="3"/>
                    </svg>
                    {activeChip}
                    <button
                      type="button"
                      className="rp-hero__chip-tag__remove"
                      aria-label={`Remove ${activeChip}`}
                      onClick={() => { setActiveChip(null); textareaRef.current?.focus(); }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  </m.div>
                ) : (
                  <m.button
                    key="expand"
                    type="button"
                    className="rp-hero__expand"
                    aria-label="Options"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.15}}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
                    </svg>
                  </m.button>
                )}
              </AnimatePresence>

              {/* Submit button */}
              <m.button
                className={`rp-hero__submit${isBuilding ? ' rp-hero__submit--loading' : ''}`}
                type="submit"
                aria-label="Start building"
                whileTap={isBuilding ? {} : {scale: 0.9}}
                disabled={isBuilding}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isBuilding ? (
                    <m.span
                      key="spinner"
                      initial={{opacity: 0, rotate: -90}}
                      animate={{opacity: 1, rotate: 0}}
                      exit={{opacity: 0}}
                      transition={{duration: 0.15}}
                    >
                      <div className="rp-spinner" />
                    </m.span>
                  ) : (
                    <m.span
                      key="arrow"
                      initial={{opacity: 0, x: -4}}
                      animate={{opacity: 1, x: 0}}
                      exit={{opacity: 0, x: 4}}
                      transition={{duration: 0.15}}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </m.span>
                  )}
                </AnimatePresence>
              </m.button>
            </div>
          </m.form>
        </div>

        {/* Category chips with icons */}
        <div className="rp-hero__chips-row">
          <button className="rp-hero__chips-nav" aria-label="Previous" onClick={() => scrollChips(-1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <div className="rp-hero__chips" ref={chipsRef}>
            {HERO_CATEGORIES.map(({icon: Icon, label}) => (
              <button
                key={label}
                className={`rp-chip-btn${activeChip === label ? ' rp-chip-btn--active' : ''}`}
                onClick={() => handleChip(label)}
                type="button"
              >
                <span className="rp-chip-icon">
                  {activeChip === label ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  ) : (
                    <Icon />
                  )}
                </span>
                <span>{label}</span>
              </button>
            ))}
          </div>
          <button className="rp-hero__chips-nav" aria-label="Next" onClick={() => scrollChips(1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>

        {/* Example prompts — change based on active category */}
        <div className="rp-hero__examples">
          <span className="rp-hero__examples-label">
            {activeChip ? `${activeChip} ideas` : 'Try an example'}
          </span>
          <div className="rp-hero__example-pills">
            <AnimatePresence mode="wait" initial={false}>
              <m.div
                key={activeChip ?? '_default'}
                className="rp-hero__example-pills-group"
                initial={{opacity: 0, y: 5}}
                animate={{opacity: 1, y: 0}}
                exit={{opacity: 0, y: -5}}
                transition={{duration: 0.18, ease: 'easeOut'}}
              >
                {(CATEGORY_PROMPTS[activeChip ?? '_default'] ?? CATEGORY_PROMPTS._default).map(ex => (
                  <button
                    key={ex}
                    className="rp-example-pill"
                    type="button"
                    onClick={() => {
                      setQuery(ex);
                      textareaRef.current?.focus();
                    }}
                  >
                    {ex}
                  </button>
                ))}
              </m.div>
            </AnimatePresence>
          </div>
        </div>
      </m.div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  LOGO STRIP
// ─────────────────────────────────────────────
const LOGOS = ['Adobe', 'Atlassian', 'ClickUp', 'Coinbase', 'Duolingo', 'Google', 'Stripe', 'Plaid'];

function LogoStrip() {
  return (
    <div className="rp-logos">
      <div className="rp-logos__inner">
        <span className="rp-logos__label">Trusted by builders at</span>
        <div className="rp-logos__list">
          {LOGOS.map(logo => (
            <span key={logo} className="rp-logo-item">{logo}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  SHOWCASE SECTION  ("Meet the Builder")
// ─────────────────────────────────────────────
function ShowcaseSection() {
  return (
    <section className="rp-showcase">
      <div className="rp-container">
        <div className="rp-showcase__header">
          <span className="rp-showcase__eyebrow">Meet the Builder</span>
          <h2 className="rp-showcase__h2">
            Creativity runs on <span>our platform</span>
          </h2>
          <p className="rp-showcase__subtitle">Everything you need. Nothing you don't.</p>
        </div>

        <div className="rp-showcase__grid">
          {/* Card 1 – Design Freely (tall, spans 2 rows) */}
          <div className="rp-sc-card rp-sc-card--design">
            <span className="rp-sc-kicker rp-sc-kicker--neutral">New Space</span>
            <h3 className="rp-sc-h3">Design Freely</h3>
            <p className="rp-sc-p">
              A new space that lets you explore and create designs directly in your app, then export them straight to your site.
            </p>
            <div className="rp-sc-oval-wrap">
              <div className="rp-sc-dashboard">
                <div className="rp-sc-dashboard__bar">
                  <div className="rp-sc-dashboard__dot" />
                  <div className="rp-sc-dashboard__dot" />
                  <div className="rp-sc-dashboard__dot" />
                </div>
                <div className="rp-sc-dashboard__body">
                  <div className="rp-sc-dashboard__row" />
                  <div className="rp-sc-dashboard__row rp-sc-dashboard__row--accent" />
                  <div className="rp-sc-dashboard__row" />
                  <div className="rp-sc-sparkline">
                    {[35,55,40,70,50,80,60,90,68,85].map((h, i) => (
                      <div
                        key={i}
                        className={`rp-sc-sparkline__bar${i >= 7 ? ' rp-sc-sparkline__bar--accent' : ''}`}
                        style={{height: `${h}%`}}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 – Move Faster */}
          <div className="rp-sc-card rp-sc-card--agents">
            <span className="rp-sc-kicker">Trusted Agents</span>
            <h3 className="rp-sc-h3">Move faster</h3>
            <p className="rp-sc-p">
              Trusted Agents run tasks together, managing progress, auth, databases, and deployments automatically.
            </p>
            <div className="rp-agents-preview">
              {['Auth', 'Database', 'Deploy', 'SEO'].map(a => (
                <div key={a} className="rp-agent-pill">
                  <span className="rp-agent-dot" />
                  {a}
                </div>
              ))}
            </div>
          </div>

          {/* Card 3 – Build Together */}
          <div className="rp-sc-card rp-sc-card--together">
            <span className="rp-sc-kicker rp-sc-kicker--light">Reports for Teams</span>
            <h3 className="rp-sc-h3">Build together</h3>
            <p className="rp-sc-p">
              Your team can focus on attending your app while the Builder handles all the heavy configuration and deploys the final product.
            </p>
          </div>

          {/* Card 4 – Ship Anything (full-width dark) */}
          <div className="rp-sc-card rp-sc-card--ship">
            <span className="rp-sc-kicker rp-sc-kicker--dark">Multiple Artifacts</span>
            <h3 className="rp-sc-h3">Ship Anything</h3>
            <p className="rp-sc-p">
              Create mobile and web apps, landing pages, and stores in one project with shared design. Deploy everything as your project scales without context switching.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  PLATFORM SECTION  ("Powered by our platform")
// ─────────────────────────────────────────────
const PLATFORM_ITEMS = [
  {
    icon: <AgentIcon />,
    eyebrow: 'Agent Chat',
    title: 'Describe It.\nPublish It.',
    desc: 'Describe and publish your project. The Agent writes production-ready code, reviews it, and deploys it on your behalf.',
  },
  {
    icon: <InfraIcon />,
    eyebrow: 'Full Stack Infrastructure',
    title: 'Build & Scale Your Apps Easily.',
    desc: 'Built-in services with zero setup: Authentication, Database, Hosting, and more. Scale apps easily and securely from day one.',
  },
  {
    icon: <PlugIcon />,
    eyebrow: 'Integrations',
    title: 'Connect To AI & Services.',
    desc: 'Enhance your apps with AI and 500+ integrations. Connect to OpenAI, Stripe, Google Workspace, and more.',
  },
  {
    icon: <ShieldPlatIcon />,
    eyebrow: 'Enterprise Control',
    title: 'Secure Your Apps As They Scale.',
    desc: 'Security controls, SOC 2 compliance, admin controls, screening, and secure workspaces keep your apps safe.',
  },
];

function PlatformSection() {
  return (
    <section className="rp-platform">
      <div className="rp-container">
        <div className="rp-platform__header">
          <h2 className="rp-platform__h2">Powered by our platform</h2>
        </div>
        <div className="rp-platform__grid">
          {PLATFORM_ITEMS.map((item, i) => (
            <m.div
              key={i}
              className="rp-platform-card"
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true, margin: '-10%'}}
              transition={{duration: 0.32, delay: i * 0.06}}
            >
              <div className="rp-platform-icon">{item.icon}</div>
              <div className="rp-platform-eyebrow">{item.eyebrow}</div>
              <h3 className="rp-platform-h3" style={{whiteSpace: 'pre-line'}}>{item.title}</h3>
              <p className="rp-platform-p">{item.desc}</p>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AgentIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}
function InfraIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="2" y="3" width="20" height="4" rx="2" />
      <rect x="2" y="10" width="20" height="4" rx="2" />
      <rect x="2" y="17" width="20" height="4" rx="2" />
    </svg>
  );
}
function PlugIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M10 2v4M14 2v4M8 6h8l-1 7H9L8 6z" />
      <path d="M12 13v5M10 18h4" />
    </svg>
  );
}
function ShieldPlatIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 3l7 3v6c0 4.5-3.1 8.6-7 9-3.9-.4-7-4.5-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

// ─────────────────────────────────────────────
//  TESTIMONIAL SECTION
// ─────────────────────────────────────────────
const TESTIMONIALS = [
  {
    quote:
      'By integrating our design and deployment tools, we\'re combining capabilities with trusted governance, helping teams move from idea to production faster and more securely than ever.',
    author: 'Ali Ghodsi',
    role: 'CEO, Databricks',
    initials: 'AG',
  },
  {
    quote:
      'This platform has completely changed how we build for clients. What used to take weeks now takes hours. The AI understands what we\'re building and just does it.',
    author: 'Sarah Chen',
    role: 'Founder, StudioLabs',
    initials: 'SC',
  },
];

function TestimonialSection() {
  const [idx, setIdx] = useState(0);
  const t = TESTIMONIALS[idx];
  const prev = () => setIdx(i => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const next = () => setIdx(i => (i + 1) % TESTIMONIALS.length);

  return (
    <section className="rp-testimonial">
      <div className="rp-container">
        <div className="rp-testimonial__inner">
          <div className="rp-testimonial__content">
            <div className="rp-testimonial__label">Trusted by builders</div>
            <blockquote className="rp-testimonial__quote">"{t.quote}"</blockquote>
            <div className="rp-testimonial__author">
              <strong>{t.author}</strong>
              {t.role}
            </div>
            <div className="rp-testimonial__nav">
              <button className="rp-testimonial__nav-btn" onClick={prev} aria-label="Previous testimonial">
                ←
              </button>
              <button className="rp-testimonial__nav-btn" onClick={next} aria-label="Next testimonial">
                →
              </button>
            </div>
          </div>

          <div className="rp-testimonial__avatar-col">
            <div className="rp-testimonial__avatar">
              <span className="rp-testimonial__initials">{t.initials}</span>
            </div>
            <span className="rp-testimonial__nav-hint">Next Testimonial</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  PRICING SECTION
// ─────────────────────────────────────────────
function PricingSection() {
  const [cycle, setCycle] = useState<UpsellBillingCycle>('monthly');
  const productsQuery = useProducts();
  const products = productsQuery.data?.products;

  return (
    <section className="rp-pricing">
      <div className="rp-container">
        <div className="rp-pricing__header">
          <h2 className="rp-pricing__h2">
            Start Small.<br />Scale Fast.
          </h2>
          <p className="rp-pricing__sub">Designed For Every Stage</p>
          <div className="rp-pricing__cycle-wrap">
            <BillingCycleRadio
              selectedCycle={cycle}
              onChange={setCycle}
              products={products}
            />
          </div>
        </div>
        <PricingTable selectedCycle={cycle} />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
//  CTA SECTION
// ─────────────────────────────────────────────
function CtaSection() {
  const {isLoggedIn} = useAuth();
  const authModal = useAuthModal();

  const handleCta = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isLoggedIn && authModal) {
      e.preventDefault();
      authModal.open({mode: 'register', redirectTo: '/new'});
    }
  };

  return (
    <section className="rp-cta">
      <div className="rp-container rp-cta__inner">
        <m.h2
          className="rp-cta__h2"
          initial={{opacity: 0, y: 8}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.35}}
        >
          What are you waiting for?
        </m.h2>
        <m.a
          href="/register"
          className="rp-btn-primary"
          onClick={handleCta}
          initial={{opacity: 0, y: 6}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.3, delay: 0.12}}
        >
          Get started free
        </m.a>
      </div>
    </section>
  );
}

